import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Suppliers.jsx.
interface SupplierRow {
  id: number;
  business_name: string;
  contact_person: string | null;
  purchase_orders_count: number;
  vendor_invoices_count: number;
}

export function Suppliers() {
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [search, setSearch] = useState('');
  const canManage = can('procurement.suppliers.manage');

  const filters = useMemo(() => ({ search: search || undefined }), [search]);
  const { data, loading, error, setPage, reload: load } = usePaginatedList<SupplierRow>('/suppliers', filters);

  const remove = async (supplier: SupplierRow) => {
    if (!(await confirmDelete(`Delete supplier "${supplier.business_name}"?`))) return;
    try {
      await api.delete(`/suppliers/${supplier.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<SupplierRow>(
      api,
      '/suppliers',
      { search: search || undefined },
      [
        { label: 'Business name', value: (r) => r.business_name },
        { label: 'Contact person', value: (r) => r.contact_person ?? '' },
        { label: 'Orders', value: (r) => r.purchase_orders_count },
        { label: 'Invoices', value: (r) => r.vendor_invoices_count },
      ],
      'suppliers.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Suppliers</h1>
        {canManage && (
          <Link className="btn btn-primary" to="/procurement/suppliers/new">
            + Add supplier
          </Link>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search business name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Business name</th>
                <th>Contact</th>
                <th className="right">Orders</th>
                <th className="right">Invoices</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((s) => (
                  <tr key={s.id}>
                    <td>{s.business_name}</td>
                    <td className="muted">{s.contact_person || '—'}</td>
                    <td className="right">{s.purchase_orders_count ?? 0}</td>
                    <td className="right">{s.vendor_invoices_count ?? 0}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/procurement/suppliers/${s.id}`} />
                      {canManage && (
                        <>
                          <IconButton icon={IconEdit} label="Edit" to={`/procurement/suppliers/${s.id}/edit`} />
                          <IconButton
                            icon={IconTrash}
                            label="Delete"
                            variant="danger-ghost"
                            onClick={() => remove(s)}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="muted center">
                    No suppliers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
